import React, { useState, useEffect, ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { getFitter } from '../library/FitterHelper';
import { isLocaleType } from '../library/LanguageListHelper';
import { Locale, LocaleType } from '../classes/Locale';
import English from '../lang/en.json';
import Korean from '../lang/kr.json';
import Japanese from '../lang/jp.json';
import French from '../lang/fr.json';
import FrenchCanadian from '../lang/fr_ca.json';
import German from '../lang/de.json';
import Spanish from '../lang/es.json';
import Italian from '../lang/it.json';
import Thai from '../lang/th.json';

interface IContextValue {
    locale: LocaleType;
    selectLocale: (locale: LocaleType) => void;
};

const defaultValue = {
    locale: Locale.english,
    selectLocale: (_: string) => {},
};

export const Context = React.createContext<IContextValue>(defaultValue);

const browserLocale = navigator.language;

let lang: Record<string, string>;

switch (browserLocale) {
    case Locale.english:
        lang = English;
        break;
    case Locale.korean:
        lang = Korean;
        break;
    case Locale.japanese:
        lang = Japanese;
        break;
    case Locale.french:
        lang = French;
        break;
    case Locale.frenchCanadian:
        lang = FrenchCanadian;
        break;
    case Locale.german:
        lang = German;
        break;
    case Locale.spanish:
        lang = Spanish;
        break;
    case Locale.italian:
        lang = Italian;
        break;
    case Locale.thai:
        lang = Thai;
        break;
    default:
        lang = English;
        break;
}

export default function IntlWrapper(props: { children: ReactNode }) {
    const [locale, setLocale] = useState<LocaleType>(Locale.english);
    const [messages, setMessages] = useState(lang);

    useEffect(() => {
        const asyncProcess = async () => {
            let fitter = await getFitter();
            let locale = fitter.locale || Locale.english;
            if (!isLocaleType(locale)) throw new Error("Invalid fitter locale");
            setMessages(getMessages(locale));
        }
        asyncProcess();
    }, []);

    function getMessages(locale: LocaleType) {
        let msgs;
        switch (locale) {
            case Locale.english:
                msgs = English;
                break;
            case Locale.korean:
                msgs = Korean;
                break;
            case Locale.japanese:
                msgs = Japanese;
                break;
            case Locale.french:
                msgs = French;
                break;
            case Locale.frenchCanadian:
                msgs = FrenchCanadian;
                break;
            case Locale.german:
                msgs = German;
                break;
            case Locale.spanish:
                msgs = Spanish;
                break;
            case Locale.italian:
                msgs = Italian;
                break;
            case Locale.thai:
                msgs = Thai;
                break;
            default:
                msgs = English;
                break;
        }
        return msgs;
    }

    function selectLocale(newLocale: LocaleType) {
        setLocale(newLocale);
        switch (newLocale) {
            case Locale.english:
                lang = English;
                break;
            case Locale.korean:
                lang = Korean;
                break;
            case Locale.japanese:
                lang = Japanese;
                break;
            case Locale.french:
                lang = French;
                break;
            case Locale.frenchCanadian:
                lang = FrenchCanadian;
                break;
            case Locale.german:
                lang = German;
                break;
            case Locale.spanish:
                lang = Spanish;
                break;
            case Locale.italian:
                lang = Italian;
                break;
            case Locale.thai:
                lang = Thai;
                break;
            default:
                lang = English;
                break;
        }
        setMessages(lang);

    }

    return (
        <Context.Provider value={{ locale, selectLocale }}>
            <IntlProvider messages={messages} locale={locale}>
                {props.children}
            </IntlProvider>
        </Context.Provider>

    );
}
